import React, { useEffect, useState } from "react";
import { i18n } from "../../translate/i18n";
import { applyHtmlTheme, clearHtmlTheme } from "../../helpers/syncHtmlTheme";
import AppSidebar from "./AppSidebar";
import AppTopbar from "./AppTopbar";
import PersonalAssistant from "../../components/PersonalAssistant";
import "./app-shell.css";

const SIDENAV_SIZE_STORAGE_KEY = "appSidenavSize";

function readDesktopSidenavSize() {
	if (typeof window === "undefined") return "default";
	const stored = localStorage.getItem(SIDENAV_SIZE_STORAGE_KEY);
	if (stored === "sm" || stored === "default") return stored;
	return "sm";
}

export default function AppLayoutShell({
	darkMode,
	children,
	mainContent,
	user,
	statesMain,
	onToggleAdmin,
	onNavClick,
	drawerClose,
	onToggleDarkMode,
	onOpenAccount,
	onLogout,
	onUpdateStatus,
}) {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [desktopSidenavSize, setDesktopSidenavSize] = useState(readDesktopSidenavSize);
	const [assistantOpen, setAssistantOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(
		typeof window !== "undefined" && window.innerWidth < 1024
	);

	const sidenavSize = isMobile ? "offcanvas" : desktopSidenavSize;

	useEffect(() => {
		applyHtmlTheme(darkMode);
		return () => clearHtmlTheme();
	}, [darkMode]);

	useEffect(() => {
		const onResize = () => {
			const mobile = window.innerWidth < 1024;
			setIsMobile(mobile);
			if (!mobile) setMobileMenuOpen(false);
		};
		onResize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		const root = document.documentElement;
		const viewport = window.visualViewport;
		const update = () => {
			const height = viewport ? viewport.height * viewport.scale : window.innerHeight;
			root.style.setProperty("--app-height", `${Math.round(height)}px`);
			if (viewport && viewport.offsetTop > 0 && viewport.scale === 1) {
				window.scrollTo(0, 0);
			}
		};
		update();
		viewport?.addEventListener("resize", update);
		viewport?.addEventListener("scroll", update);
		window.addEventListener("resize", update);
		return () => {
			viewport?.removeEventListener("resize", update);
			viewport?.removeEventListener("scroll", update);
			window.removeEventListener("resize", update);
			root.style.removeProperty("--app-height");
		};
	}, []);

	const shellClass = [
		"app-shell",
		mobileMenuOpen && isMobile ? "sidenav-enable" : "",
	].filter(Boolean).join(" ");

	const handleToggleAdmin = () => {
		onToggleAdmin();
		if (isMobile) setMobileMenuOpen(false);
	};

	const handleToggleSidenav = () => {
		if (isMobile) {
			setMobileMenuOpen((v) => !v);
			return;
		}
		setDesktopSidenavSize((prev) => {
			const next = prev === "sm" ? "default" : "sm";
			localStorage.setItem(SIDENAV_SIZE_STORAGE_KEY, next);
			return next;
		});
	};

	return (
		<div
			className={`tailwick-app ${darkMode ? "dark" : ""}`}
			data-theme={darkMode ? "dark" : "light"}
			data-sidenav-size={sidenavSize}
		>
			<div className={shellClass}>
				{isMobile && mobileMenuOpen && (
					<button
						type="button"
						className="app-shell-overlay"
						aria-label={i18n.t("master.layout.closeMenu")}
						onClick={() => setMobileMenuOpen(false)}
					/>
				)}

				<AppSidebar
					user={user}
					statesMain={statesMain}
					onToggleAdmin={handleToggleAdmin}
					onNavClick={onNavClick}
					onOpenAssistant={() => setAssistantOpen(true)}
					assistantOpen={assistantOpen}
					drawerClose={() => {
						drawerClose?.();
						if (isMobile) setMobileMenuOpen(false);
					}}
				/>

				<div className="page-content flex h-full min-w-0 flex-1 flex-col overflow-hidden">
					<AppTopbar
						onToggleSidenav={handleToggleSidenav}
						sidenavCollapsed={!isMobile && desktopSidenavSize === "sm"}
						darkMode={darkMode}
						onToggleDarkMode={onToggleDarkMode}
						onOpenAccount={onOpenAccount}
						onLogout={onLogout}
						onUpdateStatus={onUpdateStatus}
					/>
					<main className="app-main w-full">{mainContent}</main>
				</div>
			</div>
			{children}
			<PersonalAssistant open={assistantOpen} onClose={() => setAssistantOpen(false)} />
		</div>
	);
}
